import { useEffect, useMemo, useRef, useState } from "react";
import { Phone, ShieldOff, UserPlus, CircleAlert, Search, ChevronLeft, ChevronRight, Globe, Briefcase, FileBadge, X } from "lucide-react";
import { createBuddyGuarantee, revokeBuddyGuarantee } from "./buddyGuaranteeApi.js";
import { useAgentPersonRoster } from "./useAgentPersonRoster.js";
import { useBuddyActiveCounts } from "./useBuddyActiveCounts.js";
import { useSourcePersonBuddies } from "./useSourcePersonBuddies.js";
import { BuddyNetworkChart } from "./BuddyNetworkCharts.jsx";
import Pagination from "../../components/Pagination.jsx";
import PageSkeleton from "../../components/Skeleton.jsx";
import {
  describeSourcePipelineStatus,
  describeSourceTypeLabel,
  formatDateTh,
  formatRange,
  sourceKey,
  isSamePerson,
  resolveApplicantSteps,
  resolveCandidateTimelineStatus,
  RELATIONSHIP_OPTIONS,
  MAX_ACTIVE_BUDDIES,
} from "./buddyGuaranteeShared.js";

const SEARCH_DEBOUNCE_MS = 350;
const PICKER_PAGE_SIZE = 8;

function describeWriteError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ทำรายการนี้สำหรับแรงงาน/ผู้สมัครรายนี้";
  }
  if (error?.status === 409) {
    return error.message?.replace(/^Request failed with status \d+$/, "") || "รายการนี้มีการเปลี่ยนแปลงไปแล้ว กรุณาลองใหม่อีกครั้ง";
  }
  if (error?.status === 400) {
    return error.message?.replace(/^Request failed with status \d+$/, "") || "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถบันทึกรายการนี้ได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * Real, person-first picker for the SOURCE person - the real worker/
 * applicant a buddy is being added FOR (Prompt 140B-R1's own picker,
 * unchanged in shape). Shows the first real page of candidates immediately
 * on mount/tab-change, real server-side search, real Prev/Next pagination.
 */
function SourcePersonPicker({ initialSourceType = "WorkerApplication", onSelect }) {
  const [sourceType, setSourceType] = useState(initialSourceType);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const searchTimer = useRef(null);

  function handleSearchChange(value) {
    setSearchInput(value);
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    searchTimer.current = setTimeout(() => {
      setSearch(value.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
  }

  function handleTabChange(nextSourceType) {
    setSourceType(nextSourceType);
    setSearchInput("");
    setSearch("");
    setPage(1);
  }

  const roster = useAgentPersonRoster(sourceType, { enabled: true, page, pageSize: PICKER_PAGE_SIZE, search });
  const totalPages = roster.totalCount ? Math.max(1, Math.ceil(roster.totalCount / PICKER_PAGE_SIZE)) : 1;
  const range = formatRange(page, PICKER_PAGE_SIZE, roster.totalCount);

  return (
    <div className="wx-bg-picker">
      <div className="wx-bg-picker-tabs" role="group" aria-label="ประเภทบุคคล">
        <button
          type="button"
          className={sourceType === "WorkerApplication" ? "wx-bg-picker-tab wx-bg-picker-tab--active" : "wx-bg-picker-tab"}
          onClick={() => handleTabChange("WorkerApplication")}
        >
          Quick Registered
        </button>
        <button
          type="button"
          className={sourceType === "WorkerProfile" ? "wx-bg-picker-tab wx-bg-picker-tab--active" : "wx-bg-picker-tab"}
          onClick={() => handleTabChange("WorkerProfile")}
        >
          Worker Profile
        </button>
      </div>

      <label className="wx-bg-search">
        <Search size={14} aria-hidden="true" className="wx-bg-search-icon" />
        <span className="wx-visually-hidden">ค้นหาแรงงานหรือผู้สมัคร ชื่อ เบอร์โทร หรือรหัส</span>
        <input
          type="search"
          placeholder="ค้นหาชื่อ เบอร์โทร หรือรหัส..."
          value={searchInput}
          onChange={(event) => handleSearchChange(event.target.value)}
        />
      </label>

      {roster.status === "loading" && roster.items.length === 0 && <p className="wx-bg-picker-hint">กำลังโหลดรายชื่อ...</p>}
      {roster.status === "error" && <p className="wx-bg-picker-hint">ไม่สามารถโหลดรายชื่อได้ กรุณาลองใหม่อีกครั้ง</p>}
      {roster.status === "ready" && roster.items.length === 0 && (
        <p className="wx-bg-picker-hint">{search ? "ไม่พบรายชื่อที่ตรงกับคำค้นหา" : "ยังไม่มีรายชื่อประเภทนี้ในสายงานของคุณ"}</p>
      )}

      {roster.items.length > 0 && (
        <>
          {range && <p className="wx-bg-picker-range">{range}</p>}
          <ul className="wx-bg-picker-results">
            {roster.items.map((candidate) => (
              <li key={sourceKey(candidate)}>
                <button type="button" className="wx-bg-picker-result" onClick={() => onSelect(candidate)}>
                  <span className="wx-bg-picker-result-name">{candidate.name}</span>
                  <span className="wx-bg-picker-result-status">{describeSourceTypeLabel(candidate.sourceType)}</span>
                </button>
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Prompt 140C: the real BUDDY candidate picker - a person is only ever a
 * real worker/applicant in the same Agent line, never a typed-in name.
 * Reuses the exact same real, Agent-scoped `useAgentPersonRoster` the main
 * roster and the source picker both already call - since that endpoint is
 * always scoped server-side to the caller's own Agent, a candidate outside
 * the source's own line can never even appear here (cross-scope is
 * impossible by construction, not a client-side filter). `source` itself
 * is excluded from its own candidate list (self-buddy is impossible to
 * select); `existingBuddyKeys` (the source's own current real active
 * buddies, as `sourceKey`-shaped strings) disables an already-paired
 * candidate with an honest inline note instead of silently hiding them.
 */
function BuddyCandidatePicker({ source, existingBuddyKeys, onSelect }) {
  const [sourceType, setSourceType] = useState("WorkerApplication");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const searchTimer = useRef(null);

  function handleSearchChange(value) {
    setSearchInput(value);
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    searchTimer.current = setTimeout(() => {
      setSearch(value.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
  }

  function handleTabChange(nextSourceType) {
    setSourceType(nextSourceType);
    setSearchInput("");
    setSearch("");
    setPage(1);
  }

  const roster = useAgentPersonRoster(sourceType, { enabled: true, page, pageSize: PICKER_PAGE_SIZE, search });
  // Memoized on the roster's own real items array (stable - only replaced
  // when its own fetch resolves) - never recomputed on every render, which
  // would otherwise hand useBuddyActiveCounts below a brand-new array
  // reference each render and re-trigger its own fetch effect in an
  // infinite loop (the exact P140B-R2 gotcha, reproduced here once already).
  const candidates = useMemo(() => roster.items.filter((candidate) => !isSamePerson(candidate, source)), [roster.items, source]);
  const buddyCounts = useBuddyActiveCounts(candidates);
  const totalPages = roster.totalCount ? Math.max(1, Math.ceil(roster.totalCount / PICKER_PAGE_SIZE)) : 1;
  const range = formatRange(page, PICKER_PAGE_SIZE, roster.totalCount);

  return (
    <div className="wx-bg-picker">
      <div className="wx-bg-picker-tabs" role="group" aria-label="ประเภทบุคคล">
        <button
          type="button"
          className={sourceType === "WorkerApplication" ? "wx-bg-picker-tab wx-bg-picker-tab--active" : "wx-bg-picker-tab"}
          onClick={() => handleTabChange("WorkerApplication")}
        >
          Quick Registered
        </button>
        <button
          type="button"
          className={sourceType === "WorkerProfile" ? "wx-bg-picker-tab wx-bg-picker-tab--active" : "wx-bg-picker-tab"}
          onClick={() => handleTabChange("WorkerProfile")}
        >
          Worker Profile
        </button>
      </div>

      <label className="wx-bg-search">
        <Search size={14} aria-hidden="true" className="wx-bg-search-icon" />
        <span className="wx-visually-hidden">ค้นหา Buddy จากชื่อ เบอร์โทร หรือรหัส</span>
        <input
          type="search"
          placeholder="ค้นหาชื่อ เบอร์โทร หรือรหัส..."
          value={searchInput}
          onChange={(event) => handleSearchChange(event.target.value)}
        />
      </label>

      {roster.status === "loading" && candidates.length === 0 && <p className="wx-bg-picker-hint">กำลังโหลดรายชื่อ...</p>}
      {roster.status === "error" && <p className="wx-bg-picker-hint">ไม่สามารถโหลดรายชื่อได้ กรุณาลองใหม่อีกครั้ง</p>}
      {roster.status === "ready" && candidates.length === 0 && (
        <p className="wx-bg-picker-hint">{search ? "ไม่พบรายชื่อที่ตรงกับคำค้นหา" : "ยังไม่มีรายชื่อประเภทนี้ในสายงานของคุณ"}</p>
      )}

      {candidates.length > 0 && (
        <>
          {range && <p className="wx-bg-picker-range">{range}</p>}
          <ul className="wx-bg-picker-results">
            {candidates.map((candidate) => {
              const key = sourceKey(candidate);
              const alreadyPaired = existingBuddyKeys.has(key);
              const activeCount = buddyCounts.counts[key];
              const pipeline = describeSourcePipelineStatus(candidate.sourceType, candidate.pipelineStatus);
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="wx-bg-picker-result wx-bg-picker-result--candidate"
                    disabled={alreadyPaired}
                    onClick={() => onSelect(candidate)}
                  >
                    <span className="wx-bg-picker-result-main">
                      <span className="wx-bg-picker-result-name">{candidate.name}</span>
                      <span className={`wx-badge wx-badge--${pipeline.badgeVariant}`}>{pipeline.label}</span>
                    </span>
                    <span className="wx-bg-picker-result-meta">
                      {alreadyPaired ? "เป็น Buddy อยู่แล้ว" : typeof activeCount === "number" ? `Buddy ${activeCount} / ${MAX_ACTIVE_BUDDIES}` : "..."}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {totalPages > 1 && (
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Prompt 140C: the real worker-to-worker "add a buddy" flow - first
 * resolves a real SOURCE person (preselected from a row, or picked via
 * SourcePersonPicker when opened from the global "+ เพิ่ม Buddy" button),
 * then requires picking a real BUDDY candidate from the same Agent line
 * (BuddyCandidatePicker) - there is no free-text name/phone input anywhere
 * in this form anymore. The only remaining optional field is a short
 * relationship note. Self-buddy is impossible (the source is excluded from
 * its own candidate list), cross-scope is impossible (the candidate picker
 * only ever shows the same Agent-scoped roster), duplicate pairs and the
 * max-5 cap are both checked client-side before submit (for a calm inline
 * message) and re-enforced server-side regardless (a 409 either way maps
 * to the same honest error copy).
 */
export function BuddyContactForm({ source: initialSource, initialSourceType, onSuccess, onClose, titleId }) {
  const [source, setSource] = useState(initialSource ?? null);
  const [buddyCandidate, setBuddyCandidate] = useState(null);
  const [relationshipType, setRelationshipType] = useState(RELATIONSHIP_OPTIONS[0].value);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const existingBuddies = useSourcePersonBuddies(source, { buddyStatus: "Active" });
  const atCap = existingBuddies.status === "ready" && (existingBuddies.totalCount ?? 0) >= MAX_ACTIVE_BUDDIES;
  const existingBuddyKeys = new Set(
    existingBuddies.items
      .filter((buddy) => buddy.buddyPerson)
      .map((buddy) => `${buddy.buddyPerson.personType}:${buddy.buddyPerson.workerApplicationId ?? buddy.buddyPerson.workerProfileId}`)
  );

  async function handleSubmit(event) {
    event.preventDefault();
    setErrorMessage(null);

    if (!source || !buddyCandidate) {
      return;
    }

    setSubmitting(true);
    try {
      const detail = await createBuddyGuarantee({
        workerApplicationId: source.workerApplicationId,
        workerProfileId: source.workerProfileId,
        buddyWorkerApplicationId: buddyCandidate.workerApplicationId,
        buddyWorkerProfileId: buddyCandidate.workerProfileId,
        relationshipType: relationshipType || null,
      });
      onSuccess(detail, source);
    } catch (error) {
      setErrorMessage(describeWriteError(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <span className="wx-bg-drawer-kicker">เพิ่ม BUDDY</span>
      <h2 id={titleId} className="wx-bg-drawer-title">
        เพิ่ม Buddy
      </h2>
      <p className="wx-bg-drawer-desc">
        Buddy คือแรงงานหรือผู้สมัครอีกคนหนึ่งในสายงานเดียวกัน - ไม่ใช่ผู้ติดต่อภายนอกที่พิมพ์ชื่อ/เบอร์โทรเอง ระบบจะไม่ส่งการแจ้งเตือนจริงไปยังผู้ใดทั้งสิ้น
      </p>

      {!source ? (
        <SourcePersonPicker initialSourceType={initialSourceType} onSelect={setSource} />
      ) : (
        <>
          <div className="wx-bg-form-source">
            <div>
              <span className="wx-bg-form-source-label">คนในสายงาน</span>
              <strong>{source.name}</strong>
            </div>
            {!initialSource && (
              <button type="button" className="wx-button wx-button--ghost" onClick={() => setSource(null)}>
                เปลี่ยนบุคคล
              </button>
            )}
          </div>

          {existingBuddies.status === "loading" && existingBuddies.totalCount === null ? (
            <p className="wx-bg-picker-hint">กำลังตรวจสอบจำนวน Buddy ปัจจุบัน...</p>
          ) : atCap ? (
            <div className="wx-bg-cap-note" role="status">
              <CircleAlert size={16} aria-hidden="true" />
              <span>บุคคลนี้มี Buddy ที่ใช้งานอยู่ครบ {MAX_ACTIVE_BUDDIES} คนแล้วตาม policy กรุณาเพิกถอนผู้ติดต่อเดิมก่อนเพิ่มใหม่</span>
            </div>
          ) : !buddyCandidate ? (
            <>
              <p className="wx-bg-picker-section-label">เลือก Buddy จากคนในสายงานเดียวกัน</p>
              <BuddyCandidatePicker source={source} existingBuddyKeys={existingBuddyKeys} onSelect={setBuddyCandidate} />
            </>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="wx-bg-form-source wx-bg-form-source--buddy">
                <div>
                  <span className="wx-bg-form-source-label">Buddy ที่เลือก</span>
                  <strong>{buddyCandidate.name}</strong>
                  <span className="wx-bg-cell-sub">{describeSourceTypeLabel(buddyCandidate.sourceType)}</span>
                </div>
                <button type="button" className="wx-button wx-button--ghost" onClick={() => setBuddyCandidate(null)}>
                  เปลี่ยน Buddy
                </button>
              </div>

              <div className="wx-bg-form-row wx-bg-form-row--single">
                <label className="wx-bg-field">
                  <span>ความสัมพันธ์ (ไม่บังคับ)</span>
                  <select value={relationshipType} onChange={(event) => setRelationshipType(event.target.value)}>
                    {RELATIONSHIP_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {errorMessage && (
                <p className="wx-bg-form-message" role="alert">
                  {errorMessage}
                </p>
              )}

              <div className="wx-bg-drawer-foot">
                <button type="button" className="wx-button" onClick={onClose} disabled={submitting}>
                  ยกเลิก
                </button>
                <button type="submit" className="wx-button wx-button--primary" disabled={submitting}>
                  {submitting ? "กำลังบันทึก..." : "เพิ่ม Buddy"}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}

const STEP_STATE_LABEL = {
  done: "เสร็จแล้ว",
  active: "กำลังดำเนินการ",
  attention: "ต้องดำเนินการ",
  waiting: "รอดำเนินการ",
  pending: "ยังไม่เริ่ม",
  locked: "ล็อก",
};

/** Prompt 140B-R2/140C: the real onboarding-status timeline, reused for any real person (source or buddy). */
function OnboardingTimeline({ source }) {
  const steps = resolveApplicantSteps(resolveCandidateTimelineStatus(source));
  return (
    <ol className="wx-bg-stepper">
      {steps.map((step) => (
        <li key={step.key} className={`wx-bg-step wx-bg-step--${step.state}`}>
          <span className="wx-bg-step-dot" aria-hidden="true" />
          <span className="wx-bg-step-label">{step.label}</span>
          <span className="wx-bg-step-state">{STEP_STATE_LABEL[step.state]}</span>
        </li>
      ))}
    </ol>
  );
}

/** Prompt 140B-R2/140C: the real worker-identity summary strip - phone/nationality-language/skill/passport, all read straight off the normalized candidate. */
function WorkerIdentitySummary({ source }) {
  const languageAndNationality = [source.nationality, source.preferredLanguageCode].filter(Boolean).join(" · ");
  return (
    <div className="wx-bg-identity-grid">
      {source.phoneNumber && (
        <div className="wx-bg-identity-item">
          <Phone size={13} aria-hidden="true" />
          <span>{source.phoneNumber}</span>
        </div>
      )}
      {languageAndNationality && (
        <div className="wx-bg-identity-item">
          <Globe size={13} aria-hidden="true" />
          <span>{languageAndNationality}</span>
        </div>
      )}
      {source.skillName && (
        <div className="wx-bg-identity-item">
          <Briefcase size={13} aria-hidden="true" />
          <span>{source.skillName}</span>
        </div>
      )}
      {source.sourceType === "WorkerApplication" && (
        <div className="wx-bg-identity-item">
          <FileBadge size={13} aria-hidden="true" />
          <span>
            Passport {source.passportNumberMasked ?? "ยังไม่มีเลขที่หนังสือเดินทาง"} · {source.hasPassportDocument ? "แนบเอกสารแล้ว" : "ยังไม่แนบเอกสาร"}
          </span>
        </div>
      )}
    </div>
  );
}

function BuddyRow({ buddy, onRequestRevoke, revoking, confirmingRevoke, onCancelRevoke, onRevoke }) {
  const buddyPerson = buddy.buddyPerson;
  const pipeline = buddyPerson ? describeSourcePipelineStatus(buddyPerson.personType, buddyPerson.pipelineStatus) : null;
  const name = buddyPerson?.name ?? buddy.displayName;

  return (
    <div className="wx-bg-buddy-row">
      <div className="wx-bg-buddy-row-main">
        <strong>{name}</strong>
        <span className="wx-bg-buddy-row-meta">
          {pipeline ? (
            <span className={`wx-badge wx-badge--${pipeline.badgeVariant}`}>{pipeline.label}</span>
          ) : (
            <span className="wx-badge wx-badge--neutral">รายชื่อเดิม (ไม่ผูกกับบุคคลจริง)</span>
          )}
          {(buddyPerson?.phoneNumber ?? buddy.phoneNumber) && (
            <span className="wx-bg-buddy-row-contact">
              <Phone size={11} aria-hidden="true" /> {buddyPerson?.phoneNumber ?? buddy.phoneNumber}
            </span>
          )}
          {buddy.relationshipType && <span className="wx-bg-buddy-row-relationship">{buddy.relationshipType}</span>}
          {buddy.buddyStatus === "Revoked" && <span className="wx-badge wx-badge--neutral">เพิกถอนแล้ว</span>}
        </span>
      </div>
      {buddy.buddyStatus === "Revoked" ? null : confirmingRevoke ? (
        <div className="wx-bg-revoke-confirm">
          <span>ยืนยันเพิกถอนผู้ติดต่อนี้?</span>
          <button type="button" className="wx-button wx-button--ghost" onClick={onCancelRevoke}>
            ยกเลิก
          </button>
          <button type="button" className="wx-button wx-button--danger" onClick={onRevoke} disabled={revoking}>
            {revoking ? "กำลังเพิกถอน..." : "ยืนยันเพิกถอน"}
          </button>
        </div>
      ) : (
        <div className="wx-bg-buddy-row-actions">
          <button type="button" className="wx-bg-icon-button" onClick={onRequestRevoke} title="เพิกถอน" aria-label={`เพิกถอน ${name}`}>
            <ShieldOff size={14} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Prompt 140C: real buddy list + network chart + inline add/revoke,
 * factored out so it can be reused wherever a worker/applicant is shown
 * (Buddy Guarantee page's own detail drawer, ApplicantDetailDrawer.jsx,
 * AgentWorkersPage.jsx, WorkerProfilesPage.jsx). `source` only needs
 * `{ sourceType, workerApplicationId, workerProfileId, name }` - name is
 * used as the network chart's own center-node label. Revoke removes only
 * the buddy EDGE - the buddy person's own real roster row is never
 * touched, since it isn't a buddy-guarantee record at all.
 */
export function BuddyManagerSection({ source, onBuddyCountChanged, showNetworkChart = true, formPresentation = "inline" }) {
  const [showRevoked, setShowRevoked] = useState(false);
  const buddies = useSourcePersonBuddies(source, { buddyStatus: showRevoked ? undefined : "Active" });
  const [subView, setSubView] = useState(null); // null | { mode: "create" }
  const [confirmingRevokeId, setConfirmingRevokeId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [revokeError, setRevokeError] = useState(null);

  function handleWriteSuccess(detail) {
    setSubView(null);
    buddies.refetch();
    onBuddyCountChanged?.(detail?.summary?.activeCount ?? null);
  }

  async function handleRevoke(buddyId) {
    setRevokingId(buddyId);
    setRevokeError(null);
    try {
      const detail = await revokeBuddyGuarantee(buddyId);
      setConfirmingRevokeId(null);
      setRevokingId(null);
      buddies.refetch();
      onBuddyCountChanged?.(detail?.summary?.activeCount ?? null);
    } catch (revokeErr) {
      setRevokeError(describeWriteError(revokeErr));
      setRevokingId(null);
    }
  }

  const activeCount = showRevoked ? buddies.items.filter((b) => b.buddyStatus === "Active").length : buddies.totalCount ?? 0;
  const atCap = activeCount >= MAX_ACTIVE_BUDDIES;
  const showingForm = subView !== null;

  const activeBuddiesForChart = (showRevoked ? buddies.items.filter((b) => b.buddyStatus === "Active") : buddies.items)
    .filter((b) => b.buddyPerson)
    .map((b) => ({ name: b.buddyPerson.name, sourceType: b.buddyPerson.personType, pipelineStatus: b.buddyPerson.pipelineStatus }));

  // P157: in the applicant drawer the add form opens as a real DIALOG over the
  // drawer, not by replacing the list inline. Swapping a narrow drawer's whole
  // content for a multi-step picker read as "where did my applicant go?". On
  // the dedicated Buddy Guarantee page (formPresentation "inline", the default)
  // the roomy inline flow is kept unchanged.
  const asDialog = formPresentation === "dialog";

  const listBlock = (
    <>
      {atCap && (
        <p className="wx-bg-cap-inline-note">
          <CircleAlert size={13} aria-hidden="true" /> ครบจำนวนสูงสุด {MAX_ACTIVE_BUDDIES} คนตาม policy SOS buddy fan-out แล้ว
          หากต้องการเพิ่มใหม่ กรุณาเพิกถอนผู้ติดต่อเดิมก่อน
        </p>
      )}

      {/* P157: the network diagram is heavy inside a narrow applicant
          drawer and is what made the buddy area read as confusing there.
          It stays on the dedicated Buddy Guarantee page (showNetworkChart
          defaults true); the drawer passes false and keeps just the
          actionable list + add/revoke. */}
      {showNetworkChart && <BuddyNetworkChart centerPerson={source} buddies={activeBuddiesForChart} />}

      <label className="wx-bg-status-toggle wx-bg-status-toggle--inline">
        <input type="checkbox" checked={showRevoked} onChange={(event) => setShowRevoked(event.target.checked)} />
        <span>แสดงที่เพิกถอนแล้ว (audit)</span>
      </label>

      {revokeError && (
        <p className="wx-bg-form-message" role="alert">
          {revokeError}
        </p>
      )}

      {buddies.status === "loading" && buddies.items.length === 0 ? (
        <PageSkeleton variant="table" toolbar={false} label="กำลังโหลด Buddy..." />
      ) : buddies.items.length === 0 ? (
        <p className="wx-empty-list">ยังไม่มี Buddy สำหรับบุคคลนี้</p>
      ) : (
        <div className="wx-bg-buddy-list">
          {buddies.items.map((buddy) => (
            <BuddyRow
              key={buddy.buddyGuaranteeId}
              buddy={buddy}
              revoking={revokingId === buddy.buddyGuaranteeId}
              confirmingRevoke={confirmingRevokeId === buddy.buddyGuaranteeId}
              onRequestRevoke={() => setConfirmingRevokeId(buddy.buddyGuaranteeId)}
              onCancelRevoke={() => setConfirmingRevokeId(null)}
              onRevoke={() => handleRevoke(buddy.buddyGuaranteeId)}
            />
          ))}
        </div>
      )}
    </>
  );

  return (
    <section className="wx-bg-buddy-list-section">
      <div className="wx-bg-buddy-list-head">
        <h3>
          Buddy ที่ใช้งานอยู่ ({activeCount} / {MAX_ACTIVE_BUDDIES})
        </h3>
        {!showingForm && (
          <button
            type="button"
            className="wx-button wx-button--primary wx-bg-add-buddy-button"
            onClick={() => setSubView({ mode: "create" })}
            disabled={atCap}
          >
            <UserPlus size={14} aria-hidden="true" />
            เพิ่ม Buddy
          </button>
        )}
      </div>

      {asDialog ? (
        <>
          {listBlock}
          <BuddyContactDialog
            open={showingForm}
            source={source}
            onSuccess={handleWriteSuccess}
            onClose={() => setSubView(null)}
          />
        </>
      ) : showingForm ? (
        <BuddyContactForm source={source} onSuccess={handleWriteSuccess} onClose={() => setSubView(null)} />
      ) : (
        listBlock
      )}
    </section>
  );
}

/**
 * P157: the "เพิ่ม Buddy" form as a dialog over the applicant drawer.
 *
 * Mirrors WorkerDetailedProfileDialog exactly - same z-90 layer above the
 * shared DetailDrawer, same mousedown-outside-to-close (a click that starts
 * inside a search field and drifts out must not close it), same Escape key.
 * Reuses [BuddyContactForm] verbatim (kept in this file, so no import cycle),
 * so the picker/relationship/validation logic is a single implementation.
 */
function BuddyContactDialog({ open, source, onSuccess, onClose }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="wx-bg-dialog-root"
      onMouseDown={(event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) {
          onClose?.();
        }
      }}
    >
      <div className="wx-bg-dialog-backdrop" aria-hidden="true" />
      <div ref={dialogRef} className="wx-bg-dialog" role="dialog" aria-modal="true" aria-labelledby={BUDDY_DIALOG_TITLE_ID}>
        <button type="button" className="wx-bg-dialog-close" onClick={() => onClose?.()} aria-label="ปิดหน้าต่างเพิ่ม Buddy">
          <X size={16} aria-hidden="true" />
        </button>
        <div className="wx-bg-dialog-body">
          <BuddyContactForm source={source} onSuccess={onSuccess} onClose={onClose} titleId={BUDDY_DIALOG_TITLE_ID} />
        </div>
      </div>
    </div>
  );
}

const BUDDY_DIALOG_TITLE_ID = "wx-bg-add-dialog-title";

/**
 * Prompt 140C: the person-detail drawer - real worker identity summary,
 * onboarding timeline, and the real buddy network (chart + list + inline
 * add/revoke) for this exact person, via BuddyManagerSection above.
 */
export function PersonDetailView({ source, onBuddyCountChanged, onClose, titleId }) {
  const pipeline = describeSourcePipelineStatus(source.sourceType, source.pipelineStatus);
  const sourceId = source.workerApplicationId ?? source.workerProfileId;

  return (
    <div>
      <span className="wx-bg-drawer-kicker">รายละเอียดบุคคล</span>
      <h2 id={titleId} className="wx-bg-drawer-title">
        {source.name}
      </h2>
      <div className="wx-bg-source-meta">
        <span className="wx-badge wx-badge--info">{describeSourceTypeLabel(source.sourceType)}</span>
        <span className={`wx-badge wx-badge--${pipeline.badgeVariant}`}>{pipeline.label}</span>
      </div>

      <WorkerIdentitySummary source={source} />

      <div className="wx-kv-grid wx-bg-source-kv">
        <div>
          <div className="wx-kv-label">{source.sourceType === "WorkerProfile" ? "Worker Profile ID" : "Worker Application ID"}</div>
          <div className="wx-kv-value wx-bg-mono">{sourceId}</div>
        </div>
        <div>
          <div className="wx-kv-label">Agent เจ้าของสายงาน</div>
          <div className="wx-kv-value">
            {source.agentName ?? "-"}
            {source.agentCode ? ` (${source.agentCode})` : ""}
          </div>
        </div>
      </div>

      <section className="wx-bg-timeline-section">
        <h3>สถานะการเข้าสายงาน</h3>
        <OnboardingTimeline source={source} />
      </section>

      <BuddyManagerSection source={source} onBuddyCountChanged={(count) => onBuddyCountChanged(source, count)} />

      {source.createdAtUtc && <p className="wx-bg-drawer-footnote">เข้าสายงานเมื่อ {formatDateTh(source.createdAtUtc) ?? "-"}</p>}

      <div className="wx-bg-drawer-foot">
        <button type="button" className="wx-button" onClick={onClose}>
          ปิด
        </button>
      </div>
    </div>
  );
}
